import dayjs from "dayjs";
import {
  ComponentProps,
  forwardRef,
  useImperativeHandle,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { InfiniteCategorySelect } from "@components/Selects/InfiniteCategorySelect";
import { InfiniteEmployeeUsedApplicationSelect } from "@components/Selects/InfiniteEmployeeUsedApplicationSelect";

import { Button } from "@design-system/Button/Button";
import { DateRangePicker } from "@design-system/DateRangePicker/DateRangePicker";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";

type DateRange = [Date | null, Date | null];

const dateRangeToFormValues = (
  range: DateRange,
): { createdAt_Gte?: string; createdAt_Lte?: string } => {
  const [startDate, endDate] = range;
  return {
    createdAt_Gte: startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined,
    createdAt_Lte: endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
  };
};

const formValuesToDateRange = (
  createdAt_Gte?: string,
  createdAt_Lte?: string,
): DateRange => {
  return [
    createdAt_Gte ? dayjs(createdAt_Gte).toDate() : null,
    createdAt_Lte ? dayjs(createdAt_Lte).toDate() : null,
  ];
};

export type EmployeeApplicationFilterFormValues = {
  applicationResourceId?: string | undefined;
  parentCategoryId?: string | undefined;
  createdAt_Gte?: string | undefined;
  createdAt_Lte?: string | undefined;
};

export type EmployeeApplicationFilterFormRef = {
  getCurrentValues: () => EmployeeApplicationFilterFormValues;
};

type Props = {
  clientEmployeeId?: string;
  submitText?: string;
  onFilterChange: SubmitHandler<EmployeeApplicationFilterFormValues>;
  values: EmployeeApplicationFilterFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
};

export const EmployeeApplicationFilterForm = forwardRef<
  EmployeeApplicationFilterFormRef,
  Props
>(({ clientEmployeeId, onFilterChange, values, errorsAPI }, ref) => {
  const { handleSubmit, setValue, watch, getValues } =
    useForm<EmployeeApplicationFilterFormValues>({
      values: {
        applicationResourceId: values.applicationResourceId ?? undefined,
        parentCategoryId: values.parentCategoryId ?? undefined,
        createdAt_Gte: values.createdAt_Gte ?? undefined,
        createdAt_Lte: values.createdAt_Lte ?? undefined,
      },
    });

  const applicationResourceId = watch("applicationResourceId");
  const parentCategoryId = watch("parentCategoryId");
  const createdAt_Gte = watch("createdAt_Gte");
  const createdAt_Lte = watch("createdAt_Lte");

  const handleDateRangeChange = (range: DateRange) => {
    const { createdAt_Gte, createdAt_Lte } = dateRangeToFormValues(range);
    setValue("createdAt_Gte", createdAt_Gte, { shouldDirty: true });
    setValue("createdAt_Lte", createdAt_Lte, { shouldDirty: true });
  };

  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => getValues(),
    }),
    [getValues],
  );

  return (
    <Form onSubmit={handleSubmit(onFilterChange)} errorsAPI={errorsAPI}>
      {clientEmployeeId && (
        <FieldGroup label="Application">
          <InfiniteEmployeeUsedApplicationSelect
            clientEmployeeId={clientEmployeeId}
            value={applicationResourceId}
            onChange={(value) =>
              setValue("applicationResourceId", value, { shouldDirty: true })
            }
            placeholder="Search used applications..."
          />
        </FieldGroup>
      )}

      <FieldGroup label="Category">
        <InfiniteCategorySelect
          value={parentCategoryId}
          onChange={(value) =>
            setValue("parentCategoryId", value, { shouldDirty: true })
          }
          placeholder="Search and select category..."
        />
      </FieldGroup>

      <FieldGroup label="Activity between">
        <DateRangePicker
          fullWidth
          value={formValuesToDateRange(createdAt_Gte, createdAt_Lte)}
          onChange={handleDateRangeChange}
          placeholder="Select date range"
        />
      </FieldGroup>

      <Button type="submit" style={{ display: "none" }} />
    </Form>
  );
});
